import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { PHYNTEX_REGISTER, TELEGRAM_PM } from '../../constants/links';
import { MessageCircle } from 'lucide-react';

// TiltCard Component (3D hover effect)
function TiltCard({ children, className = "" }) {
  const cardRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = ((y - centerY) / centerY) * -10;
      const rotateY = ((x - centerX) / centerX) * 10;

      gsap.to(card, {
        rotateX,
        rotateY,
        duration: 0.4,
        transformPerspective: 1200,
        transformStyle: "preserve-3d",
        ease: "power2.out"
      });
    };

    const handleMouseLeave = () => {
      gsap.to(card, {
        rotateX: 0,
        rotateY: 0,
        duration: 0.6,
        ease: "power3.out"
      });
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div ref={cardRef} className={className} style={{ transformStyle: "preserve-3d" }}>
      {children}
    </div>
  );
}

function HeroSection() {
  return (
    <section
      id="hero"
      className="relative z-10 min-h-screen flex items-center justify-center px-3 md:px-10 pt-24 pb-12 overflow-hidden"
      style={{ perspective: "2000px", transformStyle: "preserve-3d" }}
      data-testid="hero-section"
    >
      <div className="max-w-6xl mx-auto w-full">
        {/* Hero Title */}
        <div className="text-center mb-8 md:mb-10">
          <span id="hero-badge" className="inline-block px-4 py-2 text-[10px] md:text-xs font-inter tracking-[0.3em] uppercase text-[#00FF88] border border-[#00FF88]/30 rounded-full mb-4 will-change-transform">
            Siapa Helloasyrol?
          </span>
          <h1
            id="hero-title"
            className="font-teko text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold uppercase leading-[0.9] tracking-tight text-[#F5F5F5] will-change-transform px-2"
            data-testid="hero-title"
          >
            Professional{" "}
            <span className="text-[#00FF88]" style={{ textShadow: "0 0 25px rgba(0,255,136,0.55)" }}>
              Trader
            </span>
          </h1>
          <p
            id="hero-sub"
            className="mt-4 text-sm sm:text-base md:text-lg text-gray-400 font-inter max-w-2xl mx-auto leading-relaxed will-change-transform px-3"
            data-testid="hero-subtitle"
          >
            Funded Trader • Entrepreneur Journey • Real Results, Real Discipline
          </p>
        </div>

        {/* 3D Profile Identity Card */}
        <div className="flex justify-center mb-8 md:mb-10">
          <TiltCard className="profile-card w-full max-w-4xl">
            <div
              className="relative rounded-2xl overflow-hidden border border-[#00FF88]/20 will-change-transform"
              style={{
                background: "linear-gradient(135deg, rgba(10,10,10,0.92) 0%, rgba(20,20,20,0.85) 100%)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 0 40px rgba(0,255,136,0.18), 0 20px 60px rgba(0,0,0,0.6)"
              }}
            >
              {/* Neon Glow Background Effect */}
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{
                  background: "radial-gradient(circle at 30% 50%, rgba(0,255,136,0.12) 0%, transparent 55%)"
                }}
              />

              <div className="relative z-10 p-5 md:p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-center">
                {/* Left: Profile Image */}
                <div className="lg:col-span-4 flex justify-center">
                  <div className="relative">
                    <div
                      className="w-40 h-40 sm:w-44 sm:h-44 md:w-52 md:h-52 lg:w-56 lg:h-56 rounded-2xl overflow-hidden border-2 border-[#00FF88]/40"
                      style={{
                        boxShadow: "0 0 30px rgba(0,255,136,0.3), 0 0 60px rgba(0,255,136,0.1)"
                      }}
                    >
                      <img
                        src="/assets/dp2.jpg"
                        alt="Asyrol"
                        className="w-full h-full object-cover"
                        data-testid="hero-profile-img"
                      />
                    </div>
                    {/* Corner Accents */}
                    <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-[#00FF88]" style={{ boxShadow: "-2px -2px 10px rgba(0,255,136,0.4)" }} />
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-[#00FF88]" style={{ boxShadow: "2px 2px 10px rgba(0,255,136,0.4)" }} />
                  </div>
                </div>

                {/* Right: Info */}
                <div className="lg:col-span-8 text-center lg:text-left">
                  <div className="mb-5">
                    <h2 className="font-teko text-3xl sm:text-4xl md:text-5xl font-bold uppercase text-[#F5F5F5] mb-2 tracking-tight">
                      Asyrol Solah
                    </h2>
                    <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                      <span className="px-3 py-1 text-[10px] md:text-xs font-inter uppercase tracking-wider text-[#00FF88] border border-[#00FF88]/30 rounded-full" style={{ background: "rgba(0,255,136,0.04)" }}>
                        Funded Trader
                      </span>
                      <span className="px-3 py-1 text-[10px] md:text-xs font-inter uppercase tracking-wider text-gray-300 border border-gray-600 rounded-full">
                        Entrepreneur
                      </span>
                      <span className="px-3 py-1 text-[10px] md:text-xs font-inter uppercase tracking-wider text-gray-300 border border-gray-600 rounded-full">
                        Mentor
                      </span>
                    </div>
                  </div>

                  <p className="text-xs sm:text-sm md:text-base text-gray-400 font-inter leading-relaxed mb-5 max-w-xl mx-auto lg:mx-0">
                    Journey of self-betterment. Konsisten improve, fokus risk management.
                    Trading bukan sprint — Trading sebuah art yang perlukan discipline & patience.
                  </p>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-2 md:gap-3">
                    {[
                      { value: "7+", label: "Years Trading" },
                      { value: "FTMO", label: "Certified" },
                      { value: "PROP", label: "Firm Trader" }
                    ].map((stat, i) => (
                      <div
                        key={i}
                        className="relative p-2 md:p-3 rounded-lg border border-[#00FF88]/15"
                        style={{ background: "rgba(0,255,136,0.03)" }}
                      >
                        <div className="font-teko text-lg sm:text-xl md:text-2xl font-bold text-[#00FF88]" style={{ textShadow: "0 0 10px rgba(0,255,136,0.4)" }}>
                          {stat.value}
                        </div>
                        <div className="text-[8px] sm:text-[9px] md:text-[10px] text-gray-500 font-inter uppercase tracking-wider">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom Accent Line */}
              <div
                className="absolute bottom-0 left-0 right-0 h-[2px]"
                style={{ background: "linear-gradient(90deg, transparent, #00FF88, transparent)", boxShadow: "0 0 8px rgba(0,255,136,0.5)" }}
              />
            </div>
          </TiltCard>
        </div>

        {/* Hero CTAs */}
        <div id="hero-cta-row" className="flex flex-col sm:flex-row gap-3 justify-center items-center will-change-transform px-3">
          <a
            href={PHYNTEX_REGISTER}
            target="_blank"
            rel="noopener noreferrer"
            className="led-pulse-btn inline-flex items-center justify-center gap-2 px-7 md:px-8 py-3.5 md:py-4 bg-[#00FF88] rounded-full text-[#050505] font-teko text-base md:text-lg tracking-wider uppercase font-bold w-full sm:w-auto"
            style={{ boxShadow: "0 0 25px rgba(0,255,136,0.45), 0 0 50px rgba(0,255,136,0.2)" }}
            data-testid="hero-cta-primary"
          >
            JOIN NOW — LEVERAGE 1:2000
          </a>
          <a
            href={TELEGRAM_PM}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 md:px-7 py-3 md:py-3.5 rounded-full text-[#F5F5F5] font-teko text-sm md:text-base tracking-wider uppercase font-bold border border-[#00FF88]/40 hover:bg-[#00FF88]/10 hover:border-[#00FF88] transition-all duration-300 w-full sm:w-auto"
            style={{ background: "rgba(10,10,10,0.6)", backdropFilter: "blur(12px)" }}
            data-testid="hero-cta-secondary"
          >
            <MessageCircle size={18} className="text-[#00FF88]" />
            PM to Join Group
          </a>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
